// Downloadable artifacts for the "Claude Design" footer note on the Treasury page.
// Everything here is generated client-side (Blob + anchor download) so the files
// can be downloaded straight from the browser — no backend required.

// --- Small helper: trigger a browser download of a text file ---
export function downloadText(
  filename: string,
  content: string,
  mime = 'text/plain;charset=utf-8',
) {
  const blob = new Blob([content], { type: mime })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  // Revoke on the next tick so the download has started.
  setTimeout(() => URL.revokeObjectURL(url), 1000)
}

// --- 1. The Claude Design handoff note (Markdown, backtick-free for safety) ---
export const CLAUDE_DESIGN_MD = [
  '# Claude Design — Handoff Note',
  '',
  'Reference bundle for Claude Design onboarding: the packet-opening animation',
  '(files + process) and the design tokens / form answers.',
  '',
  '## 1. Packet-Opening Animation',
  '',
  'The signature interaction of Treasures of the Sikh Empire: a sealed leather',
  'envelope from the Darbar is clicked, the wax "breaks", and a collectible card',
  'is revealed and filed into the collection. Pure client-side React state machine',
  'animated with framer-motion (AnimatePresence mode="wait"); icons from',
  'lucide-react. No backend.',
  '',
  'Files that implement it:',
  '  - pages/LahoreTreasuryPage.tsx -> DailyPacketSection (canonical, ~line 3939):',
  '        states sealed -> opening -> revealed -> added.',
  '  - pages/LahoreTreasuryPage.tsx -> MemberDashboardPreviewSection inline packet',
  '        (~lines 1754-2094): duplicate in the dashboard "Daily Packet" tab.',
  '  - components/TreasuryOnboarding.tsx -> "Claim Your First Packet" step',
  '        (~line 60; markup ~848-1039): states sealed -> opening -> revealed.',
  '  - pages/SponsorDeckPage.tsx -> packet slide (~lines 284-432):',
  '        states unopened -> opening -> revealed (presentational demo).',
  '',
  'Shared assets (CDN-hosted, referenced by URL):',
  '  - Envelope:    https://cdn.magicpatterns.com/uploads/9o4p7UEYp4VE7S65Drh7mS/envelope.png',
  '  - Sample card: https://cdn.magicpatterns.com/uploads/mma3UnUB3ESvFrjVFpoZd7/Moran_Sarkar.png',
  '',
  'Process (canonical DailyPacketSection):',
  "  State: useState<'sealed'|'opening'|'revealed'|'added'>('sealed')",
  '  1. SEALED   - Envelope floats (y [0,-8,0], 3.5s loop) over a pulsing gold glow.',
  '                Envelope (hover 1.04 / tap 0.96) and a lacquer-red "Break the Seal"',
  '                button (#8B0000) both start the open. onClick sets opening, then',
  '                after 1900ms sets revealed.',
  '  2. OPENING  - (~1.8s) Expanding gold burst (scale 0.4->4), envelope shakes +',
  '                shrinks and fades, 20 radial sparkle particles fly outward,',
  '                caption "The seal breaks..." fades in and out.',
  '  3. REVEALED - Card flips up (y 200->0, scale 0.5->1, rotateY 90->0, easing',
  '                [0.16,1,0.3,1], 0.9s, perspective 1000), floats + glows, shows the',
  '                "Treasury Rare" badge, name + lore, and a gold CTA "Register to Add',
  '                to Your Collection" which sets added.',
  '  4. ADDED    - (~1.1s) Card flies to the top-left archive (x -260, y -180, scale',
  '                0.18, rotate -15), sparkle trail, success check, "Opening the Darbar',
  '                gates...". A useEffect on added calls onRegister() after 1800ms and',
  '                resets to sealed.',
  '',
  'Timing constants: opening->revealed = 1900ms; added->onRegister = 1800ms.',
  'Variant differences: onboarding stops at revealed and replays on step re-entry;',
  'the sponsor-deck version uses unopened|opening|revealed and is presentational.',
  '',
  '## 2. Claude Design form answers',
  '',
  'Company name & blurb:',
  '  Life in the Sikh Kingdoms - a premium, cinematic immersive dining experience',
  '  and digital collectible platform (Treasures of the Sikh Empire) built around',
  "  Maharaja Ranjit Singh's Lahore Court and the Sikh Empire.",
  '',
  'Design system name: none formal - the visual language below is the source of truth.',
  '',
  'Typography:',
  '  - Display / headings: Playfair Display (serif; 400/500/600 + italics) -> font-serif',
  '  - Body / UI:          Inter (300/400/500) -> font-sans',
  '  - Google Fonts import (top of index.css):',
  '      family=Inter:wght@300;400;500 & family=Playfair+Display:ital,wght@0,400;0,500;0,600;1,400;1,500',
  '',
  'Color language (true brand palette is applied inline as hex):',
  '  - Antique gold (accent):   #D4AF37  (hover #E5C158)',
  '  - Near-black indigo (bg):  #0B0F19',
  '  - Deep indigo (panels):    #131B2B',
  '  - Ivory (text):            #FDFBF7',
  '  - Lacquer red (seal):      #8B0000  (hover #A00000)',
  '  - Subtle emerald: occasional highlight',
  '',
  'Aesthetic rules: solid colors (avoid gradients beyond subtle radial glows), thin',
  'gold hairline rules and corner ornaments, generous spacing, dark cinematic',
  'surfaces. No neon, no crypto/NFT styling, no casino energy. Elegant, cultural.',
  '',
  'Stack: React + TypeScript (standalone files, no framework). Tailwind CSS v3,',
  'framer-motion for animation, lucide-react for icons.',
  '',
  'Best reference files to attach:',
  '  - components/ (Navbar, Footer, HeroSection, cards)',
  '  - pages/LahoreTreasuryPage.tsx (full page + packet-opening animation)',
  '  - components/TreasuryOnboarding.tsx (multi-step modal + packet reveal)',
  '  - tailwind.config.js + index.css (tokens / fonts)',
  '',
  'Other notes: skip the .fig upload (built in code - the code is the source of',
  'truth). Fonts load from Google Fonts; assets are CDN-hosted.',
  '',
].join('\n')

// --- 2. The canonical packet-opening animation, extracted as a standalone file ---
// This is DailyPacketSection copied verbatim from pages/LahoreTreasuryPage.tsx,
// with the imports it depends on. SectionHeader is a small local helper on that
// page (eyebrow + title + description); swap in your own or a plain header.
export const COURT_RUSH_BRIEF_MD = `# Court Rush — Product & Design Brief

## Product
Court Rush by Lahore Treasury is a fast, viral group challenge. One person starts a Misl, rallies friends through WhatsApp, opens Court Packets, awakens the Five Memories of Lahore Court, and competes for free early access for their whole Misl to the Lahore Court Preview at Illuminarium Toronto in the Distillery District.

Core line: Start a Misl. Rally your people. Open the Court Gates.

Main prize: winning Misls earn free early access for confirmed members, up to 10 free seats per Misl. If a Misl has more than 10 people, the Misaldar confirms the first 10 seats; additional guests can join a waitlist.

This is a standalone, short-form experience. Exclude school mode, patron pages, discounts, access tiers, long-term campaigns, and weekly mechanics.

## Experience
The Lahore Court Preview is a live cinematic experience where guests step inside the Sikh Kingdoms through large-scale visuals, sound, portrait awakenings, storytelling, scent, and taste.

Short line: You do not just read the history. You enter it.

## V1 challenge
Users have 15 minutes to:
1. Start a Misl.
2. Rally 5 people.
3. Have each person open 1 Court Packet.
4. Reveal 2 cards per packet.
5. Awaken 3 of 5 Memories.
6. Complete a fast 3-question Court Challenge.

Top Misls win free early access for confirmed members.

## Psychology and timer
Within 10 seconds, users should understand: Get 5 people. Open packets. Awaken 3 memories. Win free seats.

Use immediate action, one CTA per screen, live group progress, visible timer, visible friends joining, instant card reward, Court Gates charging, and simple progress.

Timer copy:
- 15 minutes to open the Court Gates.
- The Court Gates are almost open. Rally one more person.
- Success: The Court Gates are open. Your Misl qualified.
- Unsuccessful: The Court stirred, but did not open this time. Start another Rush and rally again.

Never use failure, loss, streak, discount, coupon, or complex-points language.

## Required screens
1. Court Rush landing page
2. Start Misl page
3. Google login page
4. WhatsApp opt-in page
5. Share invite page
6. Join Misl page
7. Live Rush dashboard
8. Court Packet opening page
9. 2-card reveal page
10. Card contribution page
11. Memory awakened page
12. Court Gates charging page
13. Court Challenge page
14. Live leaderboard page
15. Rush result page
16. Free seat claim page
17. Misl attendance confirmation page
18. Victory share page
19. Admin dashboard

## Key screen copy
Landing hero: Start a Misl. Rally your people. Open the Court Gates.
Landing subhero: You have 15 minutes to awaken Lahore Court and win free early access for your Misl at Illuminarium Toronto.
Landing CTAs: Start Court Rush / Join a Misl.
Three steps: Rally 5 people / Open Court Packets / Win free early access.

Start Misl: Name your Misl. Then rally your people.
Fields: Misl name, Captain name, City, WhatsApp number.
CTA: Start 15-Minute Rush.

Google login: Save your cards and help your Misl open the Court Gates.
CTA: Continue with Google.

WhatsApp opt-in: Court Rush moves fast. Get updates on WhatsApp.
Subcopy: We’ll send join alerts, Court Gates progress, and seat claim reminders. No spam.
CTA: Enable WhatsApp Updates. Secondary: Skip for Now.

Share invite: Your Court Rush has started. Rally 5 people before the timer ends.
Message: Join my Misl now. We have 15 minutes to awaken Lahore Court and win free early access at Illuminarium Toronto. Open your Court Packet here: [link]
CTA: Share on WhatsApp. Secondary: Copy Invite Link.

Dashboard: show Misal name, Misaldar, countdown, members 0/5, packets opened, memories 0/3, Court Gates status, leaderboard position, recent activity, and one next move.
Example: Gill Misl is charging the Court Gates. 4/5 members joined. 2/3 memories awakened. 7:42 remaining. Next move: Rally 1 more person.
CTA: Share on WhatsApp.

Packet: Your Court Packet is ready. Reveal two cards from Lahore Court. Choose one to contribute to your Misl.
CTA: Open Court Packet.

Two-card reveal: show exactly two cards with name, Memory category, rarity, one-line lore, visual, and Contribute This Card CTA.

Contribution: Contribute this card to awaken [Memory Name]. CTA: Awaken Memory.

Memory awakened: You awakened The Envoys. Your card helped your Misl bring Lahore Court back to life.
Show increased gates glow, Misl progress, next required action.
CTAs: Return to Rush / Invite More People.

Court Gates: states Dormant, Stirring, Charging, Ready, Open. Use black-and-antique-gold Lahore Court gates, five memory symbols, Sheesh Mahal shimmer, dust particles, and heavy cinematic doors.
Copy: The Court Gates are charging. Awaken 3 memories and rally 5 members to open the gates.
CTA: Share Invite.

Court Challenge unlocks at 5 members and 3 memories. Use 3 simple, fast, group-friendly questions; no school-test feeling.
Copy: The Court Challenge is unlocked. Answer 3 questions to prove your Misl is ready to enter Lahore Court.
CTA: Begin Court Challenge.

Leaderboard only shows rank, Misl name, members joined, memories awakened, challenge complete, and time completed.
Copy: Top Misls win free early access.
CTA: Push Higher — Invite More People.

Winner result: The Court Gates are open. Your Misl won free early access to the Lahore Court Preview at Illuminarium Toronto.
CTA: Claim Free Seats.

Qualified result: Your Misl awakened Lahore Court. Free seats are limited.
CTA: Join Waitlist. Secondary: Start Another Rush.

Free seats: show winning Misl, 10-seat cap, confirmed/pending members, 24-hour claim expiry, preview date/time, QR state.
CTA: Confirm My Free Seat.

Attendance: confirmed, pending, not attending, waitlist, preview date/time, QR invite; Misaldar can manage seats.
Copy: Your Misl is entering Lahore Court together.
CTA: Send Reminder.

Victory: Our Misl opened the Court Gates. We won free early access to Lahore Court Preview at Illuminarium Toronto.
CTA: Share Victory. Secondary: Invite Waitlist.

Admin: active Rush sessions, Misls created, members joined, packets opened, cards contributed, memories awakened, challenges completed, leaderboard, winning Misls, seats claimed, pending claims, waitlist, no-show risk, WhatsApp opt-ins, invite conversion.

## Five Memories
1. The Throne — sovereignty, coinage, seals, royal authority.
2. The Court — governance, treasury, institutions, administration.
3. The Envoys — diplomacy, alliances, treaties, negotiation.
4. The Frontier — courage, protection, defence, martial memory.
5. The Inheritance — memory, loss, exile, legacy, restoration.

States: Sleeping → Stirring → Awakened. Only 3 of 5 are required.

## Visual direction
Deep black, midnight navy, antique gold, aged parchment, lacquer red, old-coin texture, Lahore Fort inspiration, Sheesh Mahal mirrorwork, royal seal motifs, gold dust in darkness, candlelit Darbar halls, carved arches, palace gates, and soft volumetric light.

Avoid generic SaaS, bright startup colors, cartoons, crypto/NFT visuals, coupon aesthetics, school pages, discount pages, and overcomplicated dashboards.

## Video placement
Landing: 5–8 second silent cinematic loop.
After first contribution: 3–5 second gates-charging animation.
Gates open: 10–15 second cinematic reveal.
Victory: shareable video/result card.
Never show a long video during sign-up.

## Intended feeling
I started a Misl. I sent it to my people. They joined fast. We opened beautiful cards. We awakened Lahore Court. The Court Gates opened. We won free access together.
`

export const PACKET_OPENING_TSX = `import React, { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { Sparkles, BookOpen } from 'lucide-react'

// NOTE: SectionHeader is a local helper in the original file — it renders an
// uppercase gold eyebrow, a serif title, and a light description. Replace it
// with your own section header if you extract this component.
//
// Shared assets:
//   envelope: https://cdn.magicpatterns.com/uploads/9o4p7UEYp4VE7S65Drh7mS/envelope.png
//   card:     https://cdn.magicpatterns.com/uploads/mma3UnUB3ESvFrjVFpoZd7/Moran_Sarkar.png

export const DailyPacketSection = ({ onRegister }: { onRegister?: () => void }) => {
  const [packetState, setPacketState] = useState<
    'sealed' | 'opening' | 'revealed' | 'added'
  >('sealed')
  // Once the "added" animation has played, hand off to registration.
  useEffect(() => {
    if (packetState !== 'added' || !onRegister) return
    const timer = setTimeout(() => {
      onRegister()
      setPacketState('sealed')
    }, 1800)
    return () => clearTimeout(timer)
  }, [packetState, onRegister])

  return (
    <section
      id="how-it-works"
      className="px-4 py-24 bg-[#131B2B] relative border-t border-[#D4AF37]/10 overflow-hidden"
    >
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,_rgba(212,175,55,0.08),_transparent_60%)]" />
      <div className="max-w-4xl mx-auto relative text-center">
        {/* SectionHeader: eyebrow="The Habit Loop", title="THE DAILY PACKET" */}

        <div className="relative mt-12 min-h-[640px] flex items-center justify-center">
          <AnimatePresence mode="wait">
            {/* SEALED */}
            {packetState === 'sealed' && (
              <motion.div
                key="sealed"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.95 }}
                transition={{ duration: 0.5 }}
                className="flex flex-col items-center text-center max-w-md mx-auto"
              >
                <motion.div
                  animate={{ y: [0, -8, 0] }}
                  transition={{ duration: 3.5, repeat: Infinity, ease: 'easeInOut' }}
                  className="relative w-64 sm:w-72 mb-10"
                >
                  <motion.button
                    type="button"
                    onClick={() => {
                      setPacketState('opening')
                      setTimeout(() => setPacketState('revealed'), 1900)
                    }}
                    whileHover={{ scale: 1.04 }}
                    whileTap={{ scale: 0.96 }}
                    aria-label="Break the seal and open your daily packet"
                    className="relative block w-full cursor-pointer"
                  >
                    <img
                      src="https://cdn.magicpatterns.com/uploads/9o4p7UEYp4VE7S65Drh7mS/envelope.png"
                      alt="Sealed envelope"
                      className="w-full drop-shadow-[0_25px_50px_rgba(0,0,0,0.7)]"
                    />
                  </motion.button>
                </motion.div>
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => {
                    setPacketState('opening')
                    setTimeout(() => setPacketState('revealed'), 1900)
                  }}
                  className="px-12 py-4 bg-[#8B0000] hover:bg-[#A00000] text-[#FDFBF7] text-xs tracking-[0.3em] uppercase font-medium shadow-2xl transition-colors flex items-center gap-3"
                >
                  <Sparkles className="w-4 h-4" /> Break the Seal
                </motion.button>
              </motion.div>
            )}

            {/* OPENING (~1.8s): gold burst + shaking/shrinking envelope + 20 sparkle particles */}
            {packetState === 'opening' && (
              <motion.div
                key="opening"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="relative flex items-center justify-center w-full h-full min-h-[560px]"
              >
                <motion.div
                  initial={{ scale: 0.4, opacity: 0 }}
                  animate={{ scale: 4, opacity: [0, 0.9, 0] }}
                  transition={{ duration: 1.8, ease: 'easeOut' }}
                  className="absolute w-96 h-96 rounded-full bg-[radial-gradient(circle,_rgba(212,175,55,0.6),_transparent_60%)] blur-3xl"
                />
                <motion.div
                  animate={{
                    scale: [1, 1.1, 0.6],
                    opacity: [1, 1, 0],
                    rotateZ: [0, -3, 4, -2, 0],
                  }}
                  transition={{ duration: 1.8, times: [0, 0.5, 1], ease: 'easeInOut' }}
                  className="relative w-64 sm:w-72 z-10"
                >
                  <img
                    src="https://cdn.magicpatterns.com/uploads/9o4p7UEYp4VE7S65Drh7mS/envelope.png"
                    alt="Opening envelope"
                    className="w-full drop-shadow-[0_0_80px_rgba(212,175,55,0.7)]"
                  />
                </motion.div>
                {Array.from({ length: 20 }).map((_, i) => {
                  const angle = (i / 20) * Math.PI * 2
                  const distance = 280
                  return (
                    <motion.div
                      key={i}
                      initial={{ x: 0, y: 0, opacity: 0, scale: 0 }}
                      animate={{
                        x: Math.cos(angle) * distance,
                        y: Math.sin(angle) * distance,
                        opacity: [0, 1, 0],
                        scale: [0, 1.2, 0],
                      }}
                      transition={{ duration: 1.6, delay: 0.3 + (i % 4) * 0.05, ease: 'easeOut' }}
                      className="absolute w-2 h-2 bg-[#D4AF37] rounded-full shadow-[0_0_14px_rgba(212,175,55,1)]"
                    />
                  )
                })}
              </motion.div>
            )}

            {/* REVEALED: card flips in, badge, lore, register CTA -> 'added' */}
            {packetState === 'revealed' && (
              <motion.div
                key="revealed"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0, y: -30 }}
                transition={{ duration: 0.6 }}
                className="flex flex-col items-center text-center max-w-2xl mx-auto"
              >
                <motion.div
                  initial={{ y: 200, scale: 0.5, rotateY: 90, opacity: 0 }}
                  animate={{ y: 0, scale: 1, rotateY: 0, opacity: 1 }}
                  transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
                  className="relative mb-6"
                  style={{ perspective: 1000 }}
                >
                  <div className="border-2 border-[#D4AF37] shadow-[0_0_50px_rgba(212,175,55,0.5)] overflow-hidden bg-[#0B0F19] w-64 sm:w-72">
                    <img
                      src="https://cdn.magicpatterns.com/uploads/mma3UnUB3ESvFrjVFpoZd7/Moran_Sarkar.png"
                      alt="Revealed card"
                      className="w-full"
                    />
                  </div>
                </motion.div>
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => setPacketState('added')}
                  className="px-10 py-4 bg-[#D4AF37] hover:bg-[#E5C158] text-[#0B0F19] text-xs tracking-[0.3em] uppercase font-bold shadow-2xl transition-colors flex items-center gap-3 mx-auto"
                >
                  <BookOpen className="w-4 h-4" /> Register to Add to Your Collection
                </motion.button>
              </motion.div>
            )}

            {/* ADDED (~1.1s): card flies to archive, then onRegister() fires after 1800ms */}
            {packetState === 'added' && (
              <motion.div
                key="added"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="flex flex-col items-center text-center max-w-md mx-auto relative"
              >
                <motion.div
                  initial={{ x: 0, y: 0, scale: 1, opacity: 1 }}
                  animate={{ x: -260, y: -180, scale: 0.18, opacity: 0, rotate: -15 }}
                  transition={{ duration: 1.1, ease: [0.7, 0, 0.84, 0] }}
                  className="relative w-44 mb-8"
                >
                  <div className="border-2 border-[#D4AF37] shadow-[0_0_40px_rgba(212,175,55,0.6)] overflow-hidden">
                    <img
                      src="https://cdn.magicpatterns.com/uploads/mma3UnUB3ESvFrjVFpoZd7/Moran_Sarkar.png"
                      alt="Revealed card"
                      className="w-full"
                    />
                  </div>
                </motion.div>
                <div className="w-16 h-16 rounded-full border-2 border-[#D4AF37] bg-[#D4AF37]/10 flex items-center justify-center mb-5">
                  <Sparkles className="w-7 h-7 text-[#D4AF37]" />
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </section>
  )
}
`
